import { z } from "zod";

/** Staff alerts: told about visits being booked, cancelled, or moved (their own, or every visit). */
export const STAFF_ALERT_KINDS = ["booked", "cancelled", "moved"] as const;
export type StaffAlertKind = (typeof STAFF_ALERT_KINDS)[number];
export const STAFF_ALERT_LABELS: Record<StaffAlertKind, string> = {
  booked: "New booking",
  cancelled: "Cancelled (or taken off my schedule)",
  moved: "Moved to a new time",
};

/**
 * `unfinished`: an online booking let go because they never added their card or deposit (so staff
 * can reach out). Unset: on by text for managers and admins (who can change clinic settings), off for others.
 */
const flags = z.object({ booked: z.boolean(), cancelled: z.boolean(), moved: z.boolean(), unfinished: z.boolean().optional() });
export const UNFINISHED_ALERT_LABEL = "Online booking not finished (no card or deposit)";
/** Whether someone gets the "booking not finished" alert this way, when they haven't chosen. */
export const unfinishedDefault = (channel: "sms" | "email", manager: boolean) => channel === "sms" && manager;
const whose = z.enum(["mine", "all"]);
export const staffAlertPrefSchema = z.object({ sms: flags, email: flags, scope: z.object({ booked: whose, cancelled: whose, moved: whose }) });
export type StaffAlertPref = z.infer<typeof staffAlertPrefSchema>;

/** A person's alerts, and whether they may choose every visit (they can see all clients). */
export interface StaffAlertView extends StaffAlertPref {
  canChooseAll: boolean;
}

/** Everyone who takes clients starts with all three by text, for their own visits. */
export const DEFAULT_STAFF_ALERTS: StaffAlertPref = {
  sms: { booked: true, cancelled: true, moved: true },
  email: { booked: false, cancelled: false, moved: false },
  scope: { booked: "mine", cancelled: "mine", moved: "mine" },
};
